import React, { useEffect, useState } from "react"
import { KSpan, KTitleSpan, KDropdown, KSelectRange, KButton, KTooltip } from "kahuna-base-react-components"
import TableNew from "../../../ui/table/TableNew"
import { getSelectedLanguage, lang } from "../../../../constants/languages"
import balanceColumns from "./balanceColumns"
import { RootState } from "../../../../store"
import { connect } from "react-redux"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { summaries } from "../../../../apis"
import {
  getCurrency,
  getLastNMonthsYYYYMM,
  getMonthRange,
  mapListToCamelCase,
  mapToCamelCase,
  yyyymmToDate
} from "../../../../utility"
import { SummaryBalance, SummaryTotals } from "../../../../types"
import Loading from "../../../ui/Loading"

const SummaryTypeOptions: KSelectOption[] = [
  { label: lang.royalties.balance.filter.DSP, value: 1, value2: "platform" },
  { label: lang.royalties.balance.filter.top_countries, value: 2, value2: "storeFront" },
  { label: lang.royalties.balance.filter.top_tracks, value: 3, value2: "topTracks" }
]

const Balance = ({
  isAdmin,
  platforms,
  selectedUser,
  selectedAccount
}: {
  isAdmin: boolean
  platforms: KSelectOption[]
  selectedUser?: KSelectOption
  selectedAccount?: KSelectOption
}) => {
  const [loading, setLoading] = useState(false)

  const [offset, setOffset] = useState(0)
  const [summaryList, setSummaryList] = useState<SummaryBalance[]>([])
  const [summaryTotals, setSummaryTotals] = useState<SummaryTotals>()
  const [selectedPlatforms, setSelectedPlatforms] = useState<KSelectOption[]>([])
  const [selectedSummaryType, setSelectedSummaryType] = useState<KSelectOption>(SummaryTypeOptions[0])
  const [selectedPeriodList, setSelectedPeriodList] = useState<string[]>(getLastNMonthsYYYYMM(3))

  const monthYear = (key: string | null | undefined) => {
    if (!key) return "N/A"
    // backend deal_date / contract_date are "YYYYMM"

    const language = getSelectedLanguage()

    const year = key.slice(0, 4)
    const month = parseInt(key.slice(4, 6), 10)
    if (!year || isNaN(month)) return "N/A"
    const d = new Date(parseInt(year, 10), month - 1, 1)
    return d.toLocaleDateString(language, { month: "short", year: "numeric" })
  }

  const processSummaryList = (list: SummaryBalance[]): SummaryBalance[] => {
    if (!list.length) return list

    const totals = list.reduce(
      (acc, item) => {
        acc.quantity += item.quantity
        acc.totalNet += item.totalNet
        return acc
      },
      { quantity: 0, totalNet: 0 }
    )

    return [
      {
        dsp: "TOTAL",
        activityPeriod: "-",
        reportingPeriod: "-",
        quantity: totals.quantity,
        totalNet: totals.totalNet,
        currency: list[0].currency
      },
      ...list
    ]
  }

  const getRoyaltiesBalance = (
    selectedPeriodList: string[],
    selectedPlatforms: KSelectOption[],
    selectedSummaryType: KSelectOption,
    summaryTotals?: SummaryTotals
  ) => {
    setLoading(true)
    const isAdminParam = isAdmin ? "&is_admin_page=1" : "&is_admin_page=0"
    const dspParam =
      selectedPlatforms.length > 0 ? `&platform_list=${selectedPlatforms.map((option) => option.value).join(",")}` : ""
    const periodParam =
      selectedPeriodList.length > 0
        ? `&period_start=${selectedPeriodList[0]}&period_end=${selectedPeriodList[selectedPeriodList.length - 1]}`
        : ""
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const totalsParam = !summaryTotals ? "&include_totals=1" : ""
    const summaryTypeParam = `&summary_type=${selectedSummaryType.value2}`
    const selectedAccountParam = `&selected_account=${selectedAccount?.value2}`

    summaries(
      `/royalties_balance/?limit=${limit}&offset=${offset}${isAdminParam}${dspParam}${periodParam}${userIdParam}${totalsParam}${summaryTypeParam}${selectedAccountParam}`,
      "get"
    )
      .then((resp) => {
        setLoading(false)
        if (!resp.data.results) {
          return
        }

        if (!summaryTotals && resp.data.totals) {
          const summaryTotals: SummaryTotals = mapToCamelCase(resp.data.totals)
          setSummaryTotals(summaryTotals)
        }

        const summaryList: SummaryBalance[] = mapListToCamelCase(resp.data.results)
        setSummaryList(processSummaryList(summaryList))
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    if (isAdmin && !selectedUser?.value) {
      return
    }
    setSummaryTotals(undefined)
    setSummaryList([])
    setSelectedPlatforms([])
    setSelectedPeriodList(getLastNMonthsYYYYMM(3))
    getRoyaltiesBalance(getLastNMonthsYYYYMM(3), [], SummaryTypeOptions[0], undefined)
  }, [selectedUser, selectedAccount])

  const limit = 10

  const topRightElements = () => {
    return (
      <div className="flex gap-4">
        <div className="flex gap-6">
          <KDropdown
            options={SummaryTypeOptions}
            onSelect={(element) => {
              const selectedSummaryType = element as KSelectOption
              setSelectedSummaryType(selectedSummaryType)
              getRoyaltiesBalance(selectedPeriodList, selectedPlatforms, selectedSummaryType, summaryTotals)
            }}
            selected={selectedSummaryType}
            rightIcon="/analytics_icons/caret-down-new.svg"
            menuWidth={150}
            width={150}
            enableIndicator={true}
          />
          {selectedPeriodList.length > 0 && (
            <div className="flex">
              <KSelectRange
                key={`filter-date-range`}
                value={[
                  yyyymmToDate(selectedPeriodList[0]),
                  yyyymmToDate(selectedPeriodList[selectedPeriodList.length - 1])
                ]}
                onChange={(event) => {
                  if (!event) {
                    setSelectedPeriodList(getLastNMonthsYYYYMM(3))
                    return
                  }
                  //@ts-ignore
                  const monthRage = getMonthRange(event[0], event[1])
                  if (monthRage.join(" ") === selectedPeriodList.join(" ")) {
                    return
                  }
                  setSelectedPeriodList(monthRage)
                  getRoyaltiesBalance(monthRage, selectedPlatforms, selectedSummaryType, summaryTotals)
                }}
                height="36px"
                width="auto"
                backgroundColor="#F7F7F7"
                hoverBackgroundColor="#F3F3F3"
                boxShadow="none"
              />
              <div className="flex ml-1 mr-2 items-center gap-1">
                <KSpan text={`${selectedPeriodList[0]}`} />
                {selectedPeriodList.length > 1 && (
                  <KSpan text={`- ${selectedPeriodList[selectedPeriodList.length - 1]}`} />
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-start max-w-[260px] min-w-[160px]">
            <KDropdown
              isClearable
              options={platforms}
              isMulti
              onSelect={(elements) => {
                setSelectedPlatforms(elements as KSelectOption[])
              }}
              placeholder={lang.analytics.all_platforms_placeholder}
              showOnlyIconsInMulti
              allowContainerShrink
              selected={selectedPlatforms}
              rightIcon="/analytics_icons/caret-down-new.svg"
              menuWidth={170}
              enableIndicator={true}
              placeholderColor="#000"
              closeMenuOnSelect={false}
              onBlur={() =>
                getRoyaltiesBalance(selectedPeriodList, selectedPlatforms, selectedSummaryType, summaryTotals)
              }
            />
          </div>
        </div>
      </div>
    )
  }

  const renderPaymentDate = (paymentDate?: string) => {
    if (!paymentDate) {
      return "-"
    }
    const language = getSelectedLanguage()
    const year = Number(paymentDate.substring(0, 4))
    const month = Number(paymentDate.substring(4, 6))

    // Last day of the month
    const date = new Date(year, month, 0)

    return new Intl.DateTimeFormat(language, {
      weekday: "short",
      day: "numeric",
      month: "long"
    }).format(date)
  }

  const renderWidget = () => {
    const openingBalance = summaryTotals?.openingBalance?.toLocaleString("en-EN", { maximumFractionDigits: 2 }) || "-"
    const newEarnings = summaryTotals?.newEarnings?.toLocaleString("en-EN", { maximumFractionDigits: 2 }) || "-"
    const advanceRecouped = summaryTotals?.advanceRecouped?.toLocaleString("en-EN", { maximumFractionDigits: 2 }) || "-"
    const advanceTotalValue = summaryTotals?.advanceTotal || 0
    const paidOut = summaryTotals?.paidOut?.toLocaleString("en-EN", { maximumFractionDigits: 2 }) || "-"
    const advanceRemainingValue = summaryTotals?.advanceRemaining || 0
    const advanceRecoupValue = summaryTotals?.advanceRecouped || 0
    const advanceProcessValue = advanceRecoupValue + advanceRemainingValue
    const closingBalance = summaryTotals?.closingBalance || 0
    let advanceProgress = 0
    if (advanceTotalValue > 0) {
      advanceProgress = Math.round(((advanceTotalValue - advanceRemainingValue) / advanceTotalValue) * 100)
    }
    const advanceRemaining =
      summaryTotals?.advanceRemaining?.toLocaleString("en-EN", { maximumFractionDigits: 2 }) || "-"
    const payout = summaryTotals?.payout?.toLocaleString("en-EN", { maximumFractionDigits: 2 }) || "-"
    const isAboveThreshold = (summaryTotals?.payout || 0) >= (summaryTotals?.paymentThreshold || 0)
    const currency = getCurrency(summaryTotals?.currency || "")
    return (
      <div className="p-4" style={{ border: "1px solid #11111120", borderRadius: 5 }}>
        {summaryTotals?.balanceTitle && (
          <div className="mb-4 mt-1">
            <KSpan
              fontSize={32}
              text={
                summaryTotals.balanceTitle.includes("Q")
                  ? summaryTotals.balanceTitle
                  : monthYear(summaryTotals.balanceTitle)
              }
            />
          </div>
        )}

        <div className="flex justify-between items-center" style={{ width: 320 }}>
          <div className="flex gap-1 items-center">
            <KTooltip
              position="bottom"
              align="right"
              children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
              width={"360px"}
              content={
                <KSpan color="#111" fontWeight={500} text={lang.royalties.balance.widget.opening_balance_tooltip} />
              }
            />
            <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.opening_balance}:`} />
          </div>
          <KSpan fontWeight={400} color="#111" text={`${currency}${openingBalance}`} />
        </div>
        <div className="flex justify-between items-center mt-2" style={{ width: 320 }}>
          <div className="flex gap-1 items-center">
            <KTooltip
              position="bottom"
              align="right"
              children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
              width={"360px"}
              content={
                <KSpan color="#111" fontWeight={500} text={lang.royalties.balance.widget.new_earnings_tooltip} />
              }
            />
            <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.new_earnings}:`} />
          </div>

          <KSpan fontWeight={400} color="#111" text={`${currency}${newEarnings}`} />
        </div>
        {advanceProcessValue > 5 &&
          ((advanceRemaining && advanceRemaining !== "0") || (advanceRecouped && advanceRecouped !== "0")) && (
            <div
              className="flex justify-between items-center mt-2 pt-2"
              style={{ width: 320, borderTop: "1px solid #111111" }}
            >
              <div className="flex gap-1 items-center">
                <KTooltip
                  position="bottom"
                  align="right"
                  children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
                  width={"360px"}
                  content={
                    <KSpan
                      color="#111"
                      fontWeight={500}
                      text={lang.royalties.balance.widget.advance_recouped_this_period_tooltip}
                    />
                  }
                />
                <KSpan
                  fontWeight={400}
                  color="#111"
                  text={`${lang.royalties.balance.widget.advance_recouped_this_period}:`}
                />
              </div>

              <KSpan fontWeight={400} color="#111" text={`${currency}${advanceRecouped}`} />
            </div>
          )}
        {advanceProcessValue > 5 &&
          ((advanceRemaining && advanceRemaining !== "0") || (advanceRecouped && advanceRecouped !== "0")) && (
            <div className="flex justify-between items-center mt-2" style={{ width: 320 }}>
              <div className="flex gap-1 items-center">
                <KTooltip
                  position="bottom"
                  align="right"
                  children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
                  width={"360px"}
                  content={
                    <KSpan
                      color="#111"
                      fontWeight={500}
                      text={lang.royalties.balance.widget.unrecouped_advance_tooltip}
                    />
                  }
                />
                <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.unrecouped_advance}:`} />
              </div>

              <KSpan fontWeight={400} color="#111" text={`${currency}${advanceRemaining}`} />
            </div>
          )}
        {advanceTotalValue > 0 &&
          advanceProcessValue > 5 &&
          ((advanceRemaining && advanceRemaining !== "0") || (advanceRecouped && advanceRecouped !== "0")) && (
            <div style={{ width: "100%", marginTop: 10 }}>
              <div
                style={{
                  position: "relative",
                  height: 12,
                  background: "#f0f0f0",
                  borderRadius: 999,
                  overflow: "hidden"
                }}
              >
                <div
                  style={{
                    width: `${Math.min(advanceProgress, 100)}%`,
                    height: "100%",
                    background: "linear-gradient(90deg, #2b2b2b, #000000)",
                    borderRadius: 999,
                    transition: "width 0.5s ease"
                  }}
                />
              </div>

              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  color: "#666"
                }}
              >
                <KSpan fontSize={12} text={`${Math.round(advanceProgress)}%`} />
                <KSpan
                  fontSize={12}
                  text={lang.royalties.balance.widget.recoup_progress
                    .replace(
                      "{recouped}",
                      `${currency}${Math.round(advanceTotalValue - advanceRemainingValue).toLocaleString("en-EN")}`
                    )
                    .replace("{total}", `${currency}${Math.round(advanceTotalValue).toLocaleString("en-EN")}`)}
                />
              </div>
            </div>
          )}
        <div
          className="flex justify-between items-center pt-2 mt-2"
          style={{ width: 320, borderTop: "1px solid #111111" }}
        >
          <div className="flex gap-1 items-center">
            <KTooltip
              position="bottom"
              align="right"
              children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
              width={"360px"}
              content={
                <KSpan color="#111" fontWeight={500} text={lang.royalties.balance.widget.payment_threshold_tooltip} />
              }
            />
            <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.payment_threshold}:`} />
          </div>

          <KSpan
            fontWeight={400}
            color="#111"
            text={`${currency}${summaryTotals?.paymentThreshold.toLocaleString("en-EN", { maximumFractionDigits: 0 })}`}
          />
        </div>
        <div className="flex justify-between items-center mt-2" style={{ width: 320 }}>
          <div className="flex gap-1 items-center">
            <KTooltip
              position="bottom"
              align="right"
              children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
              width={"360px"}
              content={
                <KSpan color="#111" fontWeight={500} text={lang.royalties.balance.widget.payment_date_tooltip} />
              }
            />
            <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.payment_date}:`} />
          </div>

          <KSpan
            fontWeight={400}
            color="#111"
            text={
              isAboveThreshold
                ? renderPaymentDate(summaryTotals?.paymentDate)
                : closingBalance < 0
                ? lang.royalties.balance.widget.recoupment_in_progress
                : lang.royalties.balance.widget.under_payment_threshold
            }
          />
        </div>
        {(summaryTotals?.paidOut || 0) > 0 && (
          <div className="flex justify-between items-center mt-2" style={{ width: 320 }}>
            <div className="flex gap-1 items-center">
              <KTooltip
                position="bottom"
                align="right"
                children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
                width={"360px"}
                content={
                  <KSpan
                    color="#111"
                    fontWeight={500}
                    text={lang.royalties.balance.widget.paid_out_this_period_tooltip}
                  />
                }
              />
              <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.paid_out_this_period}:`} />
            </div>

            <KSpan fontWeight={400} color="#111" text={`${currency}${paidOut}`} />
          </div>
        )}
        <div className="flex justify-between items-center mt-2" style={{ width: 320 }}>
          <div className="flex gap-1 items-center">
            <KTooltip
              position="bottom"
              align="right"
              children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
              width={"360px"}
              content={
                <KSpan color="#111" fontWeight={500} text={lang.royalties.balance.widget.payout_this_period_tooltip} />
              }
            />
            <KSpan fontWeight={800} color="#111" text={`${lang.royalties.balance.widget.payout_this_period}:`} />
          </div>

          <KSpan fontWeight={800} color="#111" text={`${currency}${payout}`} />
        </div>
        <div
          className="flex justify-between items-center mt-2 pt-2"
          style={{ width: 320, borderTop: "1px solid #111111" }}
        >
          <div className="flex gap-1 items-center">
            <KTooltip
              position="bottom"
              align="right"
              children={<img width="16" height="16" src="/navigation_icons/help.svg" />}
              width={"360px"}
              content={
                <KSpan color="#111" fontWeight={500} text={lang.royalties.balance.widget.closing_balance_tooltip} />
              }
            />
            <KSpan fontWeight={400} color="#111" text={`${lang.royalties.balance.widget.closing_balance}:`} />
          </div>

          <KSpan
            fontWeight={400}
            color="#111"
            text={`${currency}${closingBalance?.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="flex-grow justify-center">
      {loading && <Loading />}
      <div className="w-[100%] flex pt-6 pl-3">{renderWidget()}</div>
      <div className="w-[100%] flex ">
        <div className="grow min-h-0">
          <TableNew
            className="pt-2.5 -ml-5"
            title={<KTitleSpan fontSize={20} lineHeight="26px" text={lang.royalties.balance.title} />}
            minRowCount={limit}
            limit={limit}
            columns={balanceColumns}
            data={summaryList}
            count={1}
            loading={loading}
            topRightElement={topRightElements()}
            overrideTableOverflow="auto"
            notFoundTitle={lang.not_found.reports.title}
            notFoundDesc={lang.not_found.reports.desc}
            tablePaddingTop={6}
          />
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(Balance)
